import axios from 'axios';
import { authService } from './authService';

const PHOTO_SERVICE_BASE_URL = 'https://photo.agendario.com.br';

// Criar instância separada do axios para PhotoService
const photoApi = axios.create({
  baseURL: PHOTO_SERVICE_BASE_URL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Adicionar interceptor para incluir token JWT
photoApi.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Serviço de integração com PhotoService API
 * Upload, obtenção e deleção de fotos de perfil
 */
const photoService = {
  /**
   * Faz upload de foto de perfil do profissional
   * @param {File} file - Arquivo de imagem (já cropado)
   * @param {string} professionalId - ID do profissional
   * @returns {Promise<Object>} - { photoId, photoUrl, thumbnailUrl, originalFileSize, processedFileSize, thumbnailFileSize, dimensions, uploadedAt }
   */
  async uploadProfilePhoto(file, professionalId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('professionalId', professionalId);
    formData.append('generateThumbnail', 'false'); // Não precisamos de thumbnail
    formData.append('quality', '85');
    formData.append('maxSize', '800');

    const response = await photoApi.post('/api/photo/upload', formData);

    return response.data;
  },

  /**
   * Obtém URL da foto de perfil do usuário autenticado
   * @returns {Promise<Object>} - { userId, photoUrl }
   */
  async getMyProfilePhoto() {
    try {
      const response = await photoApi.get('/api/photo/my-photo');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Foto não encontrada
      }
      throw error;
    }
  },

  /**
   * Obtém URL da foto de perfil de um usuário específico
   * @param {string} userId - ID do usuário
   * @returns {Promise<Object>} - { userId, photoUrl }
   */
  async getUserProfilePhoto(userId) {
    try {
      const response = await photoApi.get(`/api/photo/user/${userId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Foto não encontrada
      }
      throw error;
    }
  },

  /**
   * Deleta foto de perfil do usuário autenticado
   * @returns {Promise<void>}
   */
  async deleteMyProfilePhoto() {
    await photoApi.delete('/api/photo/my-photo');
  },

  /**
   * Valida arquivo de imagem sem fazer upload
   * @param {File} file - Arquivo a validar
   * @returns {Promise<Object>} - { isValid, fileName, size, sizeKB, contentType, maxSizeMB }
   */
  async validateImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await photoApi.post('/api/photo/validate', formData);

    return response.data;
  },

  /**
   * Gera URL completa da foto de perfil do profissional
   * @param {string} userId - ID do usuário (namespace)
   * @param {string} professionalId - ID do profissional
   * @returns {string} - URL da foto
   */
  getProfilePhotoUrl(userId, professionalId) {
    // URL direta do CDN do CloudFlare R2
    // Formato: userId/professionalId.jpg (namespace de segurança)
    return `https://photos.agendario.com.br/${userId}/${professionalId}.jpg`;
  },
};

export default photoService;
